import { createHash } from "node:crypto";
import fs from "node:fs";
import { type CommonAction, response } from "@iflyrpa/share";
import axios from "axios";
import { z } from "zod";
import { USER_MESSAGE } from "../../utils/errorMessages";
import { Http } from "../../utils/http";
import { rid } from "../../utils/shipinhao/utils";
import { ShipinhaoSendMsgParamsSchema } from "./schema";

export type ShipinhaoSendMsgParams = z.infer<typeof ShipinhaoSendMsgParamsSchema>;
export { ShipinhaoSendMsgParamsSchema } from "./schema";

const CHUNK_SIZE = 512 * 1024; // 分片大小：512KB
// const COMPRESS_CONFIG = {
//     maxWidth: 828, // 最大宽度限制
//     quality: 95, // JPEG 质量（jimp 只支持 JPEG）
//     pngCompressionLevel: 6, // PNG 压缩级别（jimp 使用 filterType 或 deflateLevel）
// };

export interface UploadMsgImageResponse {
    errCode: number;
    errMsg: string;
    data?: {
        imgMsg: {
            aesKey: string;
            cdnUrl: string;
            encryptKey: string;
            height: number;
            md5: string;
            size: number;
            width: number;
        };
    };
}

// 用户信息响应
interface UserInfoResponse {
    errCode: number;
    errMsg: string;
    data?: {
        finderUser?: {
            finderUsername: string;
        };
    };
}

// async function compressImage(buffer: Buffer): Promise<{ data: Buffer; width: number; height: number }> {
//     // 使用 jimp 读取图片
//     const image = await Jimp.read(buffer);
//     const originalWidth = image.bitmap.width;
//     const originalHeight = image.bitmap.height;

//     // 计算压缩后的尺寸
//     let newWidth = originalWidth;
//     let newHeight = originalHeight;

//     if (originalWidth > COMPRESS_CONFIG.maxWidth) {
//         newWidth = COMPRESS_CONFIG.maxWidth;
//         newHeight = Math.round((originalHeight / originalWidth) * COMPRESS_CONFIG.maxWidth);
//     }

//     // 压缩处理
//     image.resize(newWidth, newHeight);

//     // 判断输出格式并设置质量
//     const mime = image.getMIME();
//     let outputBuffer: Buffer;

//     if (mime === Jimp.MIME_JPEG) {
//         // JPEG 设置质量
//         image.quality(COMPRESS_CONFIG.quality);
//         outputBuffer = await image.getBufferAsync(Jimp.MIME_JPEG);
//     } else if (mime === Jimp.MIME_PNG) {
//         // PNG 设置压缩级别（jimp 使用 deflateLevel，范围 0-9）
//         // 注意：jimp 的 PNG 压缩选项有限，filterType 控制扫描算法
//         outputBuffer = await image.getBufferAsync(Jimp.MIME_PNG);
//     } else {
//         // 其他格式默认处理为 PNG
//         outputBuffer = await image.getBufferAsync(Jimp.MIME_PNG);
//     }

//     // 输出压缩信息
//     const originalSize = buffer.length;
//     const compressedSize = outputBuffer.length;
//     console.log(
//         `file compress before ${(originalSize / 1024 / 1024).toFixed(2)}M, ` +
//             `compress after ${(compressedSize / 1024 / 1024).toFixed(2)}M, ` +
//             `dimensions: ${newWidth}x${newHeight}`,
//     );

//     return {
//         data: outputBuffer,
//         width: newWidth,
//         height: newHeight,
//     };
// }

/**
 * 获取用户信息
 */
async function getUserInfo(cookieStr: string, http: Http): Promise<UserInfoResponse> {
    const url = `https://channels.weixin.qq.com/cgi-bin/mmfinderassistant-bin/auth/auth_data?_rid=${rid()}`;

    const headers = {
        referer: "https://channels.weixin.qq.com/micro/interaction/private_msg",
        cookie: cookieStr,
        Origin: "https://channels.weixin.qq.com",
    };

    return await http.api<UserInfoResponse>(
        {
            method: "get",
            url,
            headers,
        },
        { retries: 3, retryDelay: 1000, timeout: 10000 },
    );
}

export type SendShipinhaoMsgAction = CommonAction<ShipinhaoSendMsgParams, any>;

export const shipinhaoSendMsg: SendShipinhaoMsgAction = async (_task, params) => {
    if (!params.sessionId) {
        return response(414, "sessionId 不能为空", undefined);
    }

    if (!params.toUsername) {
        return response(414, "接收者用户名不能为空", undefined);
    }

    if (!params.msgType) {
        return response(414, "消息类型不能为空", undefined);
    }

    if (!["TEXT", "IMAGE"].includes(params.msgType)) {
        return response(414, "消息类型必须是 TEXT 或 IMAGE", undefined);
    }

    if (params.msgType === "TEXT") {
        if (!params.content || params.content.trim() === "") {
            return response(414, "消息内容不能为空", undefined);
        }
    }

    if (params.msgType === "IMAGE") {
        if (!params.imageInfo?.pathOrUrl) {
            return response(414, "图片路径或URL不能为空", undefined);
        }
    }

    if (!params.extraParam) {
        return response(414, "缺少 extraParam 参数", undefined);
    }

    if (!params.extraParam.fingerPrintDeviceId || !params.extraParam.aId || !params.extraParam.uin) {
        return response(414, "fingerPrintDeviceId、aId 和 uin 不能为空", undefined);
    }

    const cookieStr = params.cookies.map((it) => `${it.name}=${it.value}`).join(";");
    const headers = {
        cookie: cookieStr,
        referer: "https://channels.weixin.qq.com/micro/interaction/private_msg",
        origin: "https://channels.weixin.qq.com",
        "content-type": "application/json",
        "finger-print-device-id": params.extraParam.fingerPrintDeviceId,
        "x-wechat-uin": params.extraParam.uin,
    };

    const http = new Http({ headers });

    const urlParams = new URLSearchParams({
        _aid: params.extraParam.aId,
        _rid: rid(),
        _pageUrl: "https://channels.weixin.qq.com/micro/interaction/private_msg",
    }).toString();

    const generateCliMsgId = () => {
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (s) => {
            const t = (Math.random() * 16) | 0;
            return (s === "x" ? t : (t & 3) | 8).toString(16);
        });
    };

    let fromUsername = params.extraParam.finderUserName;
    if (!fromUsername) {
        _task.logger.info("未提供 finderUserName，尝试获取用户信息");
        const userInfoRes = await getUserInfo(cookieStr, http);

        if (!userInfoRes.data?.finderUser?.finderUsername) {
            return response(userInfoRes.errCode || -1, userInfoRes.errMsg || "获取用户信息失败", {});
        }

        fromUsername = userInfoRes.data.finderUser.finderUsername;
        _task.logger.info(`获取到用户名: ${fromUsername}`);
    }

    let imgMsg: any;
    if (params.msgType === "IMAGE") {
        let imageBuffer: Buffer;
        const imagePath = params.imageInfo!.pathOrUrl;

        try {
            if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
                const resp = await axios.get(imagePath, { responseType: "arraybuffer" });
                imageBuffer = Buffer.from(resp.data);
            } else {
                const filePath = imagePath.startsWith("file://") ? imagePath.slice(7) : imagePath;
                imageBuffer = Buffer.from(await fs.promises.readFile(filePath));
            }
        } catch (err) {
            // 原始错误可能带本地文件路径，只记日志不返给用户
            _task.logger.error(`读取图片失败: ${err instanceof Error ? err.message : String(err)}`);
            return response(414, USER_MESSAGE.IMAGE_UPLOAD_FAILED, undefined);
        }

        // const compressedBuffer = await compressImage(imageBuffer);
        // imageBuffer = compressedBuffer.data;
        const md5 = createHash("md5").update(imageBuffer).digest("hex");
        const timestamp = Date.now().toString();
        const totalChunks = Math.ceil(imageBuffer.length / CHUNK_SIZE);

        console.log(`分片上传md5${md5}`);
        let lastRes: UploadMsgImageResponse | undefined;

        // 分片上传图片
        for (let i = 0; i < totalChunks; i++) {
            const chunkBuffer = imageBuffer.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);

            const requestData = {
                content: `data:application/octet-stream;base64,${chunkBuffer.toString("base64")}`,
                chunk: i,
                chunks: totalChunks,
                fromUsername,
                toUsername: params.toUsername,
                aesKey: "U2FsdGVkX18cwrWR73LMGhBcmAX8xoNTgbmgkZBYkEs=",
                mediaSize: imageBuffer.length,
                mediaType: 3,
                md5,
                timestamp,
                _log_finder_uin: "",
                _log_finder_id: params.extraParam.finderUserName || "",
                rawKeyBuff: null,
                pluginSessionId: null,
                scene: 7,
                reqScene: 7,
            };

            lastRes = await http.api<UploadMsgImageResponse>({
                method: "post",
                url: `https://channels.weixin.qq.com/micro/interaction/cgi-bin/mmfinderassistant-bin/private-msg/upload-media-info?${urlParams}`,
                data: requestData,
            });

            console.log(`分片上传 ${i + 1}/${totalChunks} 响应:`, lastRes);
            if (lastRes?.errCode !== 0 && i < totalChunks - 1) {
                return response(414, lastRes?.errMsg || `第 ${i + 1}/${totalChunks} 分片上传失败`, undefined);
            }
        }

        if (lastRes?.errCode !== 0) {
            return response(414, lastRes?.errMsg || "上传图片失败", undefined);
        }

        const uploadedImgMsg = lastRes.data?.imgMsg as any;
        imgMsg = {
            aeskey: uploadedImgMsg.aesKey ?? uploadedImgMsg.aeskey,
            url: uploadedImgMsg.cdnUrl ?? uploadedImgMsg.url,
            hdSize: uploadedImgMsg.hdSize ?? uploadedImgMsg.size,
            midSize: uploadedImgMsg.midSize ?? uploadedImgMsg.size,
            thumbSize: uploadedImgMsg.thumbSize ?? uploadedImgMsg.size,
            thumbHeight: uploadedImgMsg.thumbHeight ?? uploadedImgMsg.height,
            thumbWidth: uploadedImgMsg.thumbWidth ?? uploadedImgMsg.width,
            md5: uploadedImgMsg.md5,
        };
    }

    console.log("发送私信请求参数1111", {
        sessionId: params.sessionId,
        fromUsername,
    });

    const sendRes = await http.api<{ errCode: number; errMsg: string; data?: any }>({
        method: "post",
        url: `https://channels.weixin.qq.com/micro/interaction/cgi-bin/mmfinderassistant-bin/private-msg/send-private-msg?${urlParams}`,
        data: {
            timestamp: Date.now().toString(),
            _log_finder_uin: "",
            _log_finder_id: params.extraParam.finderUserName || "",
            rawKeyBuff: null,
            pluginSessionId: null,
            scene: 7,
            reqScene: 7,
            msgPack: {
                sessionId: params.sessionId,
                fromUsername,
                toUsername: params.toUsername,
                cliMsgId: generateCliMsgId(),
                msgType: params.msgType === "TEXT" ? 1 : 3,
                imgMsg: params.msgType === "IMAGE" ? imgMsg : undefined,
                textMsg: params.msgType === "TEXT" ? { content: params.content } : undefined,
            },
        },
    });

    console.log("发送私信响应:", sendRes);
    const isSuccess = sendRes?.errCode === 0;
    return response(isSuccess ? 0 : 414, sendRes?.errMsg || "发送图片私信失败", sendRes.data);
};
